import {
  Badge,
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { format } from "date-fns";
import {
  FilePlusIcon,
  FileTextIcon,
  PencilIcon,
  TrashIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  DOCUMENT_CURRENCY_SYMBOL,
  type DocumentCurrency,
  formatDocumentMoney,
  toMajorUnits,
  toMinorUnits,
} from "@/shared/lib/performance-document-currency";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import {
  installmentPlanTargetAmount,
  isLegacyTaxInclusiveInstallmentPlan,
  resolveInstallmentPaymentAmount,
  resolveInstallmentPrintAmountExTax,
} from "@/shared/lib/performance-document-payment-terms";
import type { DocumentPrintSignatureOptions } from "@/shared/lib/document-print-signature-options";
import type { DocumentDTO, InstallmentDTO } from "../../domain/contracts";
import { buildInstallmentsFromTemplate } from "../../domain/services/installment-plan-templates";
import { performanceApi } from "../api/client";
import {
  useCreatePaymentReceipt,
  useInstallmentPlanTemplatesLookup,
  useInvoicePaymentSummary,
  useQuotationInstallmentPlan,
  useUpdateInstallmentPlan,
  useVoidDocument,
} from "../api/queries";
import { IssueDocumentDialog } from "./IssueDocumentDialog";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";

const PAYMENT_RECEIPT_FORM_SCHEMA = z.object({
  amount: z.number().positive("ຕ້ອງປ້ອນຍອດຮັບ"),
  paymentMethod: z.enum(["cash", "transfer", "card"]),
  label: z.string().max(200).optional(),
  notes: z.string().max(500).optional(),
  installmentId: z.string().optional(),
});

type PaymentReceiptFormValues = z.infer<typeof PAYMENT_RECEIPT_FORM_SCHEMA>;

const INSTALLMENT_FORM_SCHEMA = z.object({
  rows: z
    .array(
      z.object({
        key: z.string(),
        installmentNo: z.number().int().positive(),
        label: z.string().max(200).optional(),
        labelEn: z.string().max(200).optional(),
        dueDate: z.string().optional(),
        amount: z.number().positive(),
        locked: z.boolean(),
      }),
    )
    .min(1),
});

type InstallmentFormValues = z.infer<typeof INSTALLMENT_FORM_SCHEMA>;
type InstallmentFormRow = InstallmentFormValues["rows"][number];

function mapInstallmentsToFormRows(
  installments: InstallmentDTO[],
  planTargetMajor: number,
  currency: DocumentCurrency,
  invoice: DocumentDTO,
): InstallmentFormRow[] {
  if (installments.length === 0) {
    return [
      {
        key: "new-1",
        installmentNo: 1,
        label: "ງວດ 1",
        labelEn: "Installment 1",
        dueDate: "",
        amount: planTargetMajor,
        locked: false,
      },
    ];
  }

  const planSum = installments.reduce((sum, row) => sum + row.amount, 0);
  const legacyInclusive = isLegacyTaxInclusiveInstallmentPlan(planSum, invoice);

  return installments.map((row) => ({
    key: row.id,
    installmentNo: row.installmentNo,
    label: row.label ?? "",
    labelEn: row.labelEn ?? "",
    dueDate: row.dueDate ? format(new Date(row.dueDate), "yyyy-MM-dd") : "",
    amount: legacyInclusive
      ? toMajorUnits(
          resolveInstallmentPrintAmountExTax(row.amount, {
            grandTotal: invoice.grandTotal,
            taxAmount: invoice.taxAmount ?? 0,
            taxEnabled: invoice.taxEnabled ?? false,
          }),
          currency,
        )
      : toMajorUnits(row.amount, currency),
    locked: row.status === "paid",
  }));
}

function balanceInstallmentRows(
  rows: InstallmentFormRow[],
  planTargetMajor: number,
  currency: DocumentCurrency,
): InstallmentFormRow[] {
  if (rows.length === 0) return rows;

  const decimals = currency === "LAK" ? 0 : 2;
  const editableIndexes = rows
    .map((row, index) => (row.locked ? -1 : index))
    .filter((index) => index >= 0);

  if (editableIndexes.length === 0) return rows;

  const lastEditableIndex = editableIndexes[editableIndexes.length - 1]!;
  const othersTotal = rows.reduce((sum, row, index) => {
    if (index === lastEditableIndex) return sum;
    return sum + (row.amount || 0);
  }, 0);

  const nextAmount = Number((planTargetMajor - othersTotal).toFixed(decimals));

  return rows.map((row, index) =>
    index === lastEditableIndex
      ? { ...row, amount: Math.max(nextAmount, currency === "LAK" ? 1 : 0.01) }
      : row,
  );
}

function buildInstallmentPlanInputFromTemplate(
  template: Parameters<typeof buildInstallmentsFromTemplate>[0],
  invoice: DocumentDTO,
  currency: DocumentCurrency,
  baseDate: Date,
) {
  const planTargetMajor = toMajorUnits(
    installmentPlanTargetAmount(invoice),
    currency,
  );
  return buildInstallmentsFromTemplate(template, planTargetMajor, baseDate).map(
    (row) => ({
      installmentNo: row.installmentNo,
      label: row.label,
      labelEn: row.labelEn,
      dueDate: row.dueDate,
      amount: row.amount,
    }),
  );
}

function formatMoney(value: number, currency: DocumentCurrency = "LAK") {
  return formatDocumentMoney(value, currency);
}

function currencySymbol(currency: DocumentCurrency = "LAK") {
  return DOCUMENT_CURRENCY_SYMBOL[currency];
}

const RECEIPT_STATUS_LABELS: Record<DocumentDTO["status"], string> = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  issued: "ອອກແລ້ວ",
  partial: "ຊຳລະບາງສ່ວນ",
  paid: "ຊຳລະແລ້ວ",
  voided: "ຍົກເລີກ",
};

function nextPendingInstallment(installments: InstallmentDTO[]) {
  return (
    installments.find(
      (row) => row.status === "pending" || row.status === "overdue",
    ) ?? null
  );
}

function installmentPlanSum(installments: InstallmentDTO[]) {
  return installments.reduce((sum, row) => sum + row.amount, 0);
}

function suggestedPaymentAmountMinor(
  invoice: DocumentDTO,
  installments: InstallmentDTO[],
  pending: InstallmentDTO | null,
  remainingAmount: number,
) {
  if (pending) {
    return resolveInstallmentPaymentAmount(invoice, pending.amount, {
      planSum: installmentPlanSum(installments),
    });
  }
  return remainingAmount;
}

function installmentPayableLabelAmount(
  invoice: DocumentDTO,
  row: InstallmentDTO,
  installments: InstallmentDTO[],
) {
  return resolveInstallmentPaymentAmount(invoice, row.amount, {
    planSum: installmentPlanSum(installments),
  });
}

function CreatePaymentReceiptDialog({
  open,
  onOpenChange,
  eventId,
  invoiceId,
  invoice,
  remainingAmount,
  installments,
  currency,
  hideInstallmentPicker = false,
  installmentTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  invoiceId: string;
  invoice: DocumentDTO;
  remainingAmount: number;
  installments: InstallmentDTO[];
  currency: DocumentCurrency;
  hideInstallmentPicker?: boolean;
  installmentTitle?: string;
}) {
  const pending = nextPendingInstallment(installments);
  const createReceipt = useCreatePaymentReceipt(eventId, invoiceId);
  const symbol = currencySymbol(currency);

  const methods = RHF.useForm<PaymentReceiptFormValues>({
    resolver: zodResolver(PAYMENT_RECEIPT_FORM_SCHEMA),
    defaultValues: {
      amount: toMajorUnits(
        suggestedPaymentAmountMinor(
          invoice,
          installments,
          pending,
          remainingAmount,
        ),
        currency,
      ),
      paymentMethod: "cash",
      label: pending?.label ?? undefined,
      notes: "",
      installmentId: pending?.id,
    },
  });

  const selectedInstallmentId = methods.watch("installmentId");

  useEffect(() => {
    if (!open) return;
    const next = nextPendingInstallment(installments);
    methods.reset({
      amount: toMajorUnits(
        suggestedPaymentAmountMinor(
          invoice,
          installments,
          next,
          remainingAmount,
        ),
        currency,
      ),
      paymentMethod: "cash",
      label: next?.label ?? undefined,
      notes: "",
      installmentId: next?.id,
    });
  }, [open, installments, remainingAmount, currency, methods, invoice]);

  useEffect(() => {
    if (!open || !selectedInstallmentId) return;
    const row = installments.find((item) => item.id === selectedInstallmentId);
    if (!row) return;
    methods.setValue(
      "amount",
      toMajorUnits(
        installmentPayableLabelAmount(invoice, row, installments),
        currency,
      ),
    );
    methods.setValue("label", row.label ?? undefined);
  }, [open, selectedInstallmentId, installments, invoice, currency, methods]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {installmentTitle
              ? `ຮັບຊຳລະ — ${installmentTitle}`
              : "ຮັບຊຳລະ / ອອກໃບຮັບເງິນ"}
          </DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => {
            createReceipt.mutate(
              {
                amount: values.amount,
                paymentMethod: values.paymentMethod,
                label: values.label,
                notes: values.notes,
                installmentId: values.installmentId,
              },
              {
                onSuccess: () => onOpenChange(false),
              },
            );
          }}
          className="space-y-4"
        >
          <FormPrice
            name="amount"
            currencySymbol={symbol}
            label="ຍອດຮັບຄັ້ງນີ້"
            hint={`ຄ້າງຊຳລະ: ${formatMoney(remainingAmount, currency)}`}
          />
          <FormSelect
            name="paymentMethod"
            label="ຊ່ອງທາງຊຳລະ"
            options={[
              { value: "cash", label: "ເງິນສົດ" },
              { value: "transfer", label: "ໂອນ" },
              { value: "card", label: "ບັດ" },
            ]}
          />
          {installments.length > 0 && !hideInstallmentPicker && (
            <FormSelect
              name="installmentId"
              label="ງວດ"
              options={installments
                .filter(
                  (row) => row.status === "pending" || row.status === "overdue",
                )
                .map((row) => ({
                  value: row.id,
                  label: `ງວດ ${row.installmentNo} — ${formatMoney(
                    installmentPayableLabelAmount(invoice, row, installments),
                    currency,
                  )}`,
                }))}
            />
          )}
          <FormInput name="label" label="ລາຍລະອຽດບັນທຶກ" />
          <FormInput name="notes" label="ໝາຍເຫດ" />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={createReceipt.isPending}>
              {createReceipt.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງໃບຮັບເງິນຮ່າງ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

function InstallmentPlanDialog({
  open,
  onOpenChange,
  eventId,
  invoiceId,
  invoice,
  installments,
  currency,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  invoiceId: string;
  invoice: DocumentDTO;
  installments: InstallmentDTO[];
  currency: DocumentCurrency;
}) {
  const updatePlan = useUpdateInstallmentPlan(eventId, invoiceId);
  const templatesLookup = useInstallmentPlanTemplatesLookup();
  const defaultTemplate =
    templatesLookup.data?.items.find((t) => t.isDefault) ??
    templatesLookup.data?.items[0];
  const symbol = currencySymbol(currency);
  const planTargetMinor = installmentPlanTargetAmount(invoice);
  const planTargetMajor = toMajorUnits(planTargetMinor, currency);
  const hasPaidInstallments = installments.some((row) => row.status === "paid");

  const methods = RHF.useForm<InstallmentFormValues>({
    resolver: zodResolver(INSTALLMENT_FORM_SCHEMA),
    defaultValues: {
      rows: mapInstallmentsToFormRows(
        installments,
        planTargetMajor,
        currency,
        invoice,
      ),
    },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      rows: mapInstallmentsToFormRows(
        installments,
        planTargetMajor,
        currency,
        invoice,
      ),
    });
  }, [open, currency, planTargetMajor, installments, methods, invoice]);

  const rows = methods.watch("rows");
  const plannedTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (row.amount || 0), 0),
    [rows],
  );
  const amountTolerance = currency === "LAK" ? 1 : 0.01;
  const totalsMatch =
    Math.abs(plannedTotal - planTargetMajor) <= amountTolerance;

  const setRows = (next: InstallmentFormRow[]) => {
    methods.setValue("rows", next, { shouldDirty: true, shouldValidate: true });
  };

  const loadFromDefaultTemplate = () => {
    if (!defaultTemplate) return;
    if (hasPaidInstallments) return;

    const built = buildInstallmentPlanInputFromTemplate(
      defaultTemplate,
      invoice,
      currency,
      new Date(),
    );
    setRows(
      built.map((row) => ({
        key: `template-${row.installmentNo}`,
        installmentNo: row.installmentNo,
        label: row.label ?? "",
        labelEn: row.labelEn ?? "",
        dueDate: format(row.dueDate, "yyyy-MM-dd"),
        amount: row.amount,
        locked: false,
      })),
    );
  };

  const addRow = () => {
    const nextNo =
      rows.reduce((max, row) => Math.max(max, row.installmentNo), 0) + 1;
    const remaining = Number((planTargetMajor - plannedTotal).toFixed(2));
    setRows([
      ...rows,
      {
        key: `new-${nextNo}-${Date.now()}`,
        installmentNo: nextNo,
        label: `ງວດ ${nextNo}`,
        labelEn: `Installment ${nextNo}`,
        dueDate: "",
        amount: remaining > 0 ? remaining : amountTolerance,
        locked: false,
      },
    ]);
  };

  const removeRow = (index: number) => {
    const target = rows[index];
    if (!target || target.locked || rows.length <= 1) return;
    const next = balanceInstallmentRows(
      rows.filter((_, i) => i !== index),
      planTargetMajor,
      currency,
    );
    setRows(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ແກ້ໄຂແຜນງວດຊຳລະ</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => {
            if (!totalsMatch) return;
            updatePlan.mutate(
              {
                installments: values.rows.map((row) => ({
                  installmentNo: row.installmentNo,
                  label: row.label || undefined,
                  labelEn: row.labelEn || undefined,
                  dueDate: row.dueDate ? new Date(row.dueDate) : null,
                  amount: row.amount,
                })),
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-4"
        >
          <p className="text-muted-foreground text-xs">
            {invoice.taxEnabled
              ? "ຍອດລວມທຸກງວດຕ້ອງເທົ່າຍອດກ່ອນ VAT — ງວດທີ່ຊຳລະແລ້ວແກ້ຍອດບໍ່ໄດ້"
              : "ຍອດລວມທຸກງວດຕ້ອງເທົ່າຍອດໃບເກັບເງິນ — ງວດທີ່ຊຳລະແລ້ວແກ້ຍອດບໍ່ໄດ້"}
          </p>
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {rows.map((row, index) => (
              <div
                key={row.key}
                className="space-y-2 rounded-lg border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-sm">ງວດ {row.installmentNo}</p>
                    {row.locked && (
                      <Badge variant="default">ຊຳລະແລ້ວ</Badge>
                    )}
                  </div>
                  {!row.locked && rows.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => removeRow(index)}
                    >
                      <TrashIcon className="size-4" />
                      ລຶບ
                    </Button>
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <FormInput
                    name={`rows.${index}.label`}
                    label="ປ້າຍ (ລາວ)"
                  />
                  <FormInput
                    name={`rows.${index}.labelEn`}
                    label="Label (EN)"
                  />
                  <FormInput
                    name={`rows.${index}.dueDate`}
                    label="ຄົບກຳນົດ"
                    type="date"
                  />
                  <FormPrice
                    currencySymbol={symbol}
                    name={`rows.${index}.amount`}
                    label="ຍອດ"
                    disabled={row.locked}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              {invoice.taxEnabled ? "ຍອດກ່ອນ VAT" : "ຍອດໃບເກັບເງິນ"}:{" "}
              {formatMoney(planTargetMinor, currency)}
            </span>
            <span
              className={
                totalsMatch
                  ? "font-medium text-primary"
                  : "font-medium text-destructive"
              }
            >
              ລວມແຜນ:{" "}
              {formatDocumentMoney(
                toMinorUnits(plannedTotal, currency),
                currency,
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              ເພີ່ມງວດ
            </Button>
            {defaultTemplate && !hasPaidInstallments && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadFromDefaultTemplate}
              >
                ໂຫຼດຈາກແມ່ແບບເລີ່ມຕົ້ນ
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={updatePlan.isPending || !totalsMatch}
            >
              {updatePlan.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກແຜນ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

function QuotationInstallmentPaymentContext({
  installment,
  currency,
}: {
  installment: InstallmentDTO;
  currency: DocumentCurrency;
}) {
  return (
    <div className="rounded-lg border bg-muted/10 p-3">
      <p className="font-medium text-sm">
        ງວດ {installment.installmentNo}
        {installment.label ? ` — ${installment.label}` : ""}
      </p>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
        <span>
          ຄົບກຳນົດ:{" "}
          {installment.dueDate ? formatDateLocal(installment.dueDate) : "—"}
        </span>
        <span>ຍອດງວດ: {formatMoney(installment.amount, currency)}</span>
      </div>
      <p className="mt-2 text-muted-foreground text-xs">
        INV ນີ້ອອກຈາກແຜນງວດໃບສະເໜີລາຄາ — ຮັບຊຳລະແລ້ວອອກໃບຮັບເງິນ
      </p>
    </div>
  );
}

export function InvoicePaymentPanel({
  eventId,
  invoice,
  printSignatureOptions,
}: {
  eventId: string;
  invoice: DocumentDTO;
  printSignatureOptions?: DocumentPrintSignatureOptions;
}) {
  const canCreate = useActionPermission(["performance-documents:create"]);
  const canIssue = useActionPermission(["performance-documents:issue"]);
  const canVoid = useActionPermission(["performance-documents:void"]);
  const currency = invoice.currency ?? "LAK";

  const summaryQuery = useInvoicePaymentSummary(eventId, invoice.id);
  const voidDocument = useVoidDocument(eventId);

  const isQuotationInstallmentInvoice = Boolean(
    invoice.sourceInstallmentId && invoice.parentDocumentId,
  );
  const quotationPlanQuery = useQuotationInstallmentPlan(
    eventId,
    isQuotationInstallmentInvoice ? invoice.parentDocumentId : null,
  );
  const sourceQuotationInstallment = useMemo(() => {
    if (!invoice.sourceInstallmentId) return null;
    return (
      quotationPlanQuery.data?.installments.find(
        (row) => row.id === invoice.sourceInstallmentId,
      ) ?? null
    );
  }, [invoice.sourceInstallmentId, quotationPlanQuery.data?.installments]);

  const [createOpen, setCreateOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [issueReceiptTarget, setIssueReceiptTarget] =
    useState<DocumentDTO | null>(null);

  const summary = summaryQuery.data;
  const receipts = summary?.receipts ?? [];
  const installments = summary?.installments ?? [];
  const progressPercent = summary?.progressPercent ?? 0;
  const remainingAmount = invoice.remainingAmount ?? 0;
  const paidAmount = invoice.paidAmount ?? 0;
  const pendingInstallments = installments.filter(
    (row) => row.status === "pending" || row.status === "overdue",
  );
  const singlePendingInstallment =
    pendingInstallments.length === 1 ? pendingInstallments[0] : null;
  const installmentTitle = sourceQuotationInstallment
    ? `ງວດ ${sourceQuotationInstallment.installmentNo}${
        sourceQuotationInstallment.label
          ? ` — ${sourceQuotationInstallment.label}`
          : ""
      }`
    : singlePendingInstallment
      ? `ງວດ ${singlePendingInstallment.installmentNo}${
          singlePendingInstallment.label
            ? ` — ${singlePendingInstallment.label}`
            : ""
        }`
      : undefined;
  const hideInstallmentPicker =
    isQuotationInstallmentInvoice || pendingInstallments.length <= 1;

  return (
    <div className="mt-4 space-y-4 rounded-lg border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-sm">{invoice.documentNo}</p>
            <Badge variant={invoice.status === "paid" ? "default" : "outline"}>
              {RECEIPT_STATUS_LABELS[invoice.status] ?? invoice.status}
            </Badge>
            <Badge variant="outline">{currency}</Badge>
          </div>
          <p className="mt-1 text-muted-foreground text-xs">
            {isQuotationInstallmentInvoice
              ? `ຍອດງວດນີ້ ${formatMoney(invoice.grandTotal, currency)}`
              : `ຍອດສັນຍາ ${formatMoney(invoice.grandTotal, currency)}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && remainingAmount > 0 && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <FilePlusIcon className="size-4" />
              ຮັບຊຳລະ
            </Button>
          )}
          {canCreate && !isQuotationInstallmentInvoice && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPlanOpen(true)}
            >
              <PencilIcon className="size-4" />
              {installments.length > 0 ? "ແກ້ໄຂແຜນງວດ" : "ກຳນົດແຜນງວດ"}
            </Button>
          )}
          <PerformanceBrandPrintMenu
            label="ພິມ INV"
            signatureOptions={printSignatureOptions}
            signatureToggle={
              printSignatureOptions
                ? undefined
                : {
                    defaultChecked:
                      invoice.status !== "draft" && invoice.status !== "voided",
                  }
            }
            buildPrintUrl={(lang, options) =>
              performanceApi.documents.printUrl(invoice.id, lang, options)
            }
          />
        </div>
      </div>

      {sourceQuotationInstallment ? (
        <QuotationInstallmentPaymentContext
          installment={sourceQuotationInstallment}
          currency={currency}
        />
      ) : isQuotationInstallmentInvoice && quotationPlanQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດຂໍ້ມູນງວດ...</p>
      ) : null}

      <div>
        <div className="mb-1 flex justify-between text-xs">
          <span className="text-muted-foreground">
            {isQuotationInstallmentInvoice
              ? "ຄວາມຄືບໜ້າການຊຳລະງວດນີ້"
              : "ຄວາມຄືບໜ້າການຊຳລະ"}
          </span>
          <span className="font-medium">
            {formatMoney(paidAmount, currency)} /{" "}
            {formatMoney(invoice.grandTotal, currency)} ({progressPercent}%)
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {remainingAmount > 0 && (
          <p className="mt-1 text-muted-foreground text-xs">
            ຄ້າງຊຳລະ {formatMoney(remainingAmount, currency)}
          </p>
        )}
      </div>

      <div>
        <p className="mb-2 font-medium text-sm">ໃບຮັບເງິນ</p>
        <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                ໃບຮັບເງິນ
              </th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                ຍອດ
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                ສະຖານະ
              </th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                ຈັດການ
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {receipts.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  ຍັງບໍ່ມີໃບຮັບເງິນ
                </td>
              </tr>
            ) : (
              receipts.map((receipt) => (
                <tr key={receipt.id}>
                  <td className="px-3 py-2">{receipt.documentNo}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatMoney(
                      receipt.grandTotal,
                      receipt.currency ?? currency,
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline">
                      {RECEIPT_STATUS_LABELS[receipt.status] ?? receipt.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {receipt.status === "draft" && canIssue && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIssueReceiptTarget(receipt)}
                        >
                          <FileTextIcon className="size-4" />
                          ອອກ
                        </Button>
                      )}
                      {receipt.status !== "draft" &&
                        receipt.status !== "voided" && (
                          <PerformanceBrandPrintMenu
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            showLabel={false}
                            signatureOptions={printSignatureOptions}
                            signatureToggle={
                              printSignatureOptions
                                ? undefined
                                : {
                                    defaultChecked:
                                      receipt.status !== "draft" &&
                                      receipt.status !== "voided",
                                  }
                            }
                            buildPrintUrl={(lang, options) =>
                              performanceApi.documents.printUrl(
                                receipt.id,
                                lang,
                                options,
                              )
                            }
                          />
                        )}
                      {receipt.status === "draft" && canVoid && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={voidDocument.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: "ຍົກເລີກໃບຮັບເງິນ",
                              description: "ຢືນຢັນຍົກເລີກໃບຮັບເງິນຮ່າງ",
                              actionText: "ຍົກເລີກ",
                              ActionProps: { variant: "destructive" },
                            });
                            if (ok) {
                              voidDocument.mutate({
                                documentId: receipt.id,
                                reason: "ຍົກເລີກຈາກໜ້າຮັບຊຳລະ",
                              });
                            }
                          }}
                        >
                          {voidDocument.isPending ? "ກຳລັງຍົກເລີກ..." : "ຍົກເລີກ"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <CreatePaymentReceiptDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        eventId={eventId}
        invoiceId={invoice.id}
        invoice={invoice}
        remainingAmount={remainingAmount}
        installments={installments}
        currency={currency}
        hideInstallmentPicker={hideInstallmentPicker}
        installmentTitle={installmentTitle}
      />
      {!isQuotationInstallmentInvoice && (
        <InstallmentPlanDialog
          open={planOpen}
          onOpenChange={setPlanOpen}
          eventId={eventId}
          invoiceId={invoice.id}
          invoice={invoice}
          installments={installments}
          currency={currency}
        />
      )}

      <IssueDocumentDialog
        eventId={eventId}
        document={issueReceiptTarget}
        open={issueReceiptTarget !== null}
        onOpenChange={(open) => {
          if (!open) setIssueReceiptTarget(null);
        }}
      />
    </div>
  );
}
